import React, { useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Pagination,
  useTheme,
  TableContainer,
} from "@mui/material";
import moment from "moment";
import PaginationComponent from "components/ui-components/PaginationComponent";
import { useGetDueThisMonthQuery } from "store/slices/apis/keyClearancesDashboardSlice";
import { useParams } from "react-router-dom";
import { styled } from "@mui/system";

// Styled Table Head Cell
const StyledTableHead = styled(TableCell)({
  fontWeight: 500,
  fontSize: "14px",
  color: "#505258",
  padding: "10px 14px",
  backgroundColor: "#F8F8F8",
  border: "1px solid #D8D8D8",
  textAlign: "center",
  whiteSpace: "nowrap",
});

// Styled Table Body Cell
const StyledTableBody = styled(TableCell)({
  fontWeight: 400,
  fontSize: "14px",
  color: "#5052588A",
  padding: "10px 14px",
  backgroundColor: "#FFFFFF",
  border: "1px solid #D8D8D8",
  textAlign: "center",
  whiteSpace: "nowrap",
});

const KeyClearancesDueThisMonth = () => {
  const { projectId } = useParams();
  const theme = useTheme();
  const [pageNumber, setPageNumber] = useState(1);
  const pageSize = 12;
  const { data, isFetching } = useGetDueThisMonthQuery({
    projectId,
    page: pageNumber,
    pageSize: pageSize
  });
  const keyList = Array.isArray(data?.data)
    ? data.data.map((item, idx) => ({
      ...item,
      sr_no: (pageNumber - 1) * pageSize + idx + 1,
    }))
    : [];

  return (
    <Box
      sx={{
        width: "100%",
        border: "1px solid #D8D8D8",
        borderRadius: "12px",
        overflowX: 'hidden',
        backgroundColor: "#fff",
        height: "100%",
        position: "relative",
        paddingBottom: "75px"
      }}
    >
      <Box sx={{ p: 2 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 500,
            fontSize: "18px",
            color: theme?.customSettings?.themeColor,
            letterSpacing: "0.4px",
            marginBottom: "5.5px"
          }}
        >
          Key Clearances Due This Month
        </Typography>
        <Typography sx={{ mb: 1, color: "#666" }}>
          Critical approvals that are scheduled to be obtained within the current calendar month
        </Typography>
      </Box>
      <TableContainer>
        <Table >
          <TableHead>
            <TableRow>
              {[
                "Sl No",
                "Clearance Name",
                "Approving Authority",
                "Due Date",
                "Days Left",
              ].map((header, index, array) => (
                <StyledTableHead
                  key={header}
                  sx={{
                    borderLeft: index === 0 ? "none" : undefined,
                    borderRight: index === array.length - 1 ? "none" : undefined,
                  }}
                >
                  {header}
                </StyledTableHead>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isFetching ? (
              <TableRow>
                <StyledTableBody colSpan={5}>Loading...</StyledTableBody>
              </TableRow>
            ) : keyList.length > 0 ? (
              keyList.map((row, index) => {
                const cells = [
                  row?.sr_no,
                  row?.approval_compliance_description,
                  row?.approving_authority_agency,
                  row?.plan_date ? moment(row?.plan_date).format("DD/MM/YYYY") : "N.A.",
                  (() => {
                    if (!row?.plan_date) return "N.A.";
                    const daysLeft = moment(row.plan_date).diff(moment(), "days");
                    if (daysLeft > 0) return `${daysLeft}`;
                    else if (daysLeft === 0) return "Due today";
                    else return `Overdue (${Math.abs(daysLeft)})`;
                  })()
                ];

                return (
                  <TableRow key={index}>
                    {cells.map((cellContent, cellIndex) => (
                      <StyledTableBody
                        key={cellIndex}
                        sx={{
                          borderLeft: cellIndex === 0 ? "none" : undefined,
                          borderRight: cellIndex === cells.length - 1 ? "none" : undefined
                        }}
                      >
                        {cellContent}
                      </StyledTableBody>
                    ))}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <StyledTableBody colSpan={5}>
                  {isFetching ? "Loading..." : "No record found."}
                </StyledTableBody>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {
        !isFetching && (
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              right: 0,
              padding: "12px 16px",
              backgroundColor: "#fff",
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <PaginationComponent
              pageNumber={data?.pagination?.currentPage || 1}
              totalPages={data?.pagination?.totalPages || 1}
              onPageChange={setPageNumber}
            />
          </Box>
        )
      }
    </Box >
  );

};

export default KeyClearancesDueThisMonth;
